/**
 * iRoll Order Form JavaScript — v2026-06-02
 * Handles multi-step form navigation, multi-product selection, shipping calculations,
 * rental date picker, rental pricing, and form submission
 */
/* iroll-order-form v2.1 emailcheck */

(function() {
  'use strict';

  // ═══════════════════════════════════════════
  // SHIPPING COST MATRIX
  // ═══════════════════════════════════════════
  const shippingCosts = {
    // Electric Scooters
    'small': {  // Air Pro, Climber 2
      'western-cape': 200,
      'other': 300
    },
    'medium': {  // S1F, Dualtron Togo, Dualtron Forever
      'western-cape': 250,
      'other': 350
    },
    'large': {  // RS Jet
      'western-cape': 400,
      'other': 700
    },
    // Electric Unicycles
    'unicycle-medium': {  // King Song 14D, V9, N12
      'western-cape': 250,
      'other': 450
    },
    'unicycle-large': {  // V12S, V14 Pro, S16
      'western-cape': 300,
      'other': 600
    },
    // Electric Motorcycles
    'motorcycle': {  // AP-2C, AP-15 Pro
      'western-cape': 400,
      'other': 800
    },
    // Accessories
    'accessory': {  // Tyres, rims, etc
      'western-cape': 200,
      'other': 300
    }
  };

  // Shipping category priority (higher = more expensive)
  const categoryPriority = {
    'small': 1,
    'unicycle-medium': 2,
    'medium': 3,
    'unicycle-large': 4,
    'large': 5,
    'motorcycle': 6,
    'accessory': 7
  };

  // Default category if product not matched
  const defaultCategory = 'medium';

  // ═══════════════════════════════════════════
  // RENTAL PRICING
  // ═══════════════════════════════════════════
  function calculateRentalPrice(days, weeklyPrice, monthlyPrice) {
    if (days <= 0) return { price: 0, variant: 'weekly', periods: 0, label: '-' };

    if (days <= 14) {
      const weeks = Math.ceil(days / 7);
      return {
        price: weeks * weeklyPrice,
        variant: 'weekly',
        periods: weeks,
        label: weeks === 1 ? '1 week' : weeks + ' weeks'
      };
    } else {
      const months = Math.ceil(days / 30);
      return {
        price: months * monthlyPrice,
        variant: 'monthly',
        periods: months,
        label: months === 1 ? '1 month' : months + ' months'
      };
    }
  }

  function calculateDurationDays(startDate, endDate) {
    const ms = endDate - startDate;
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  }

  function getSelectedRentalVariant(handle) {
    const radio = document.querySelector(`input[name="rental-variant-${handle}"]:checked`);
    if (!radio) return null;
    return {
      id: radio.value,
      title: radio.dataset.variantTitle,
      price: parseInt(radio.dataset.variantPrice) || 0,
      sku: radio.dataset.variantSku
    };
  }

  function getSelectedProductVariant(handle) {
    const radio = document.querySelector(`input[name="variant-${handle}"]:checked`);
    if (!radio) return null;
    return {
      id: radio.value,
      title: radio.dataset.variantTitle,
      price: parseInt(radio.dataset.variantPrice) || 0,
      sku: radio.dataset.variantSku
    };
  }

  function getRentalVariantPrices(handle) {
    const container = document.querySelector(`input[name="rental-variant-${handle}"]`)?.closest('.ir-order-form__variant-selector');
    if (!container) return { weekly: 0, monthly: 0 };
    const radios = container.querySelectorAll('input[type="radio"]');

    let weekly = 0;
    let monthly = 0;

    radios.forEach(r => {
      const title = (r.dataset.variantTitle || '').toLowerCase();
      const price = parseInt(r.dataset.variantPrice) || 0;
      if (title.includes('week')) weekly = price / 100;
      if (title.includes('month')) monthly = price / 100;
    });

    return { weekly, monthly };
  }

  // ═══════════════════════════════════════════
// ═══════════════════════════════════════════
// ATTRIBUTION CAPTURE (UTM + Referrer)
// ═══════════════════════════════════════════
(function captureAttribution() {
  const STORAGE_KEY = 'iroll_attribution';
  // Match Google Ads GCLID conversion window (90 days)
  const SESSION_MINUTES = 90 * 24 * 60;

  function getUrlParams() {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_term: params.get('utm_term') || '',
      utm_content: params.get('utm_content') || '',
      gclid: params.get('gclid') || '',
      fbclid: params.get('fbclid') || '',
    };
  }

  function detectSourceFromReferrer() {
    const ref = document.referrer || '';
    if (!ref) return 'direct';
    try {
      const url = new URL(ref);
      const host = url.hostname.toLowerCase();
      if (host.includes('google')) return 'google-organic';
      if (host.includes('facebook') || host.includes('fb.')) return 'meta';
      if (host.includes('instagram')) return 'instagram';
      if (host.includes('youtube')) return 'youtube';
      if (host.includes('whatsapp')) return 'whatsapp';
      // Own domain — internal navigation, not a referral
      if (host.includes('iroll.co.za')) return 'direct';
      return 'referral';
    } catch (e) { return 'direct'; }
  }

  function buildAttribution() {
    const url = getUrlParams();
    if (url.gclid) { url.utm_source = url.utm_source || 'google'; url.utm_medium = url.utm_medium || 'cpc'; }
    if (url.fbclid) { url.utm_source = url.utm_source || 'facebook'; url.utm_medium = url.utm_medium || 'paid_social'; }
    let source = url.utm_source || detectSourceFromReferrer();
    const sourceMap = {
      'google': url.gclid ? 'google-ads' : 'google-organic',
      'adwords': 'google-ads',
      'facebook': 'meta',
      'fb': 'meta',
      'instagram': 'instagram',
      'direct': 'direct',
      'whatsapp': 'whatsapp',
      'email': 'email',
      'newsletter': 'email',
    };
    return {
      source: sourceMap[source.toLowerCase()] || source,
      medium: url.utm_medium || '',
      campaign: url.utm_campaign || '',
      term: url.utm_term || '',
      content: url.utm_content || '',
      gclid: url.gclid,
      fbclid: url.fbclid,
      referrer: document.referrer || '',
      landing_page: window.location.href.split('?')[0],
      captured_at: new Date().toISOString(),
    };
  }

  function saveAttribution(data) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...data, stored_at: Date.now() })); } catch (e) {}
  }

  function loadAttribution() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (Date.now() - (data.stored_at || 0) > SESSION_MINUTES * 60 * 1000) {
        localStorage.removeItem(STORAGE_KEY); return null;
      }
      return data;
    } catch (e) { return null; }
  }

  const urlParams = getUrlParams();
  let data;
  // Fresh attribution ONLY if a new click ID (gclid/fbclid) is present in the URL.
  // UTM params alone should NOT overwrite a stored GCLID from a previous page.
  // Otherwise preserve cached attribution (prevents internal nav from overwriting
  // a stored Google Ads GCLID with a generic 'direct' or 'referral' source).
  if (urlParams.gclid || urlParams.fbclid) {
    data = buildAttribution();
  } else {
    data = loadAttribution();
    if (!data) {
      data = buildAttribution();
    }
  }
  saveAttribution(data);

  // Inject hidden fields into the form
  const formEl = document.getElementById('iroll-order-form');
  if (formEl) {
    const fields = [
      { name: 'attribution_source', value: data.source },
      { name: 'attribution_medium', value: data.medium },
      { name: 'attribution_campaign', value: data.campaign },
      { name: 'attribution_gclid', value: data.gclid || '' },
      { name: 'attribution_referrer', value: data.referrer || '' },
    ];
    fields.forEach(f => {
      let input = formEl.querySelector(`input[name="${f.name}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = f.name;
        formEl.appendChild(input);
      }
      input.value = f.value;
    });
  }
  // Expose for payload builder
  window.irollAttribution = data;
})();
  // DOM ELEMENTS
  // ═══════════════════════════════════════════
  const form = document.getElementById('iroll-order-form');
  if (!form) return;

  const elements = {
    // Product selection fields
    productCheckboxes: document.querySelectorAll('input[name="product-checkbox"]'),
    productsHidden: document.getElementById('order-products'),
    productsDetailsHidden: document.getElementById('order-products-details'),
    productSelector: document.getElementById('product-selector'),

    // Selected products preview
    selectedProductsPreview: document.getElementById('selected-products-preview'),
    selectedProductsList: document.getElementById('selected-products-list'),
    selectedCount: document.getElementById('selected-count'),
    productsSubtotal: document.getElementById('products-subtotal'),

    fulfillmentRadios: document.querySelectorAll('input[name="Fulfillment Method"]'),
    deliveryOption: document.getElementById('delivery-option'),
    provinceField: document.getElementById('province-field'),
    provinceSelect: document.getElementById('order-province'),
    shippingCostDisplay: document.getElementById('shipping-cost-display'),
    shippingCostValue: document.getElementById('shipping-cost-value'),
    shippingCostHidden: document.getElementById('shipping-cost-hidden'),
    deliveryPriceBadge: document.getElementById('delivery-price-badge'),

    // Summary
    orderSummary: document.getElementById('order-summary'),
    summaryProduct: document.getElementById('summary-product'),
    summaryProductPrice: document.getElementById('summary-product-price'),
    summaryShippingRow: document.getElementById('summary-shipping-row'),
    summaryShipping: document.getElementById('summary-shipping'),
    summaryTotal: document.getElementById('summary-total'),

    // Step 2 fields
    addressFields: document.getElementById('address-fields'),
    businessCheckbox: document.getElementById('business-purchase'),
    businessFields: document.getElementById('business-fields'),
    nameInput: document.getElementById('order-name'),
    emailInput: document.getElementById('order-email'),
    phoneInput: document.getElementById('order-phone'),
    streetInput: document.getElementById('order-street'),
    suburbInput: document.getElementById('order-suburb'),
    cityInput: document.getElementById('order-city'),
    postalInput: document.getElementById('order-postal'),
    companyInput: document.getElementById('order-company'),

    // Tabs
    tabs: document.querySelectorAll('.ir-order-form__tab'),
    orderTypeHidden: document.getElementById('order-type'),

    // Rental elements (Step 1)
    rentalFulfillment: document.getElementById('rental-fulfillment'),
    rentalUberFields: document.getElementById('rental-uber-fields'),
    rentalCollectionOption: document.getElementById('rental-collection-option'),
    rentalUberOption: document.getElementById('rental-uber-option'),
    rentalDeliveryAddress: document.getElementById('rental-delivery-address'),
    rentalDeliverySuburb: document.getElementById('rental-delivery-suburb'),
    rentalDeliveryCity: document.getElementById('rental-delivery-city'),

    // Rental elements (Step 2)
    rentalFieldsStep2: document.getElementById('rental-fields-step2'),
    rentalStartDate: document.getElementById('rental-start-date'),
    rentalDaysInput: document.getElementById('rental-days-input'),
    rentalDaysMinus: document.getElementById('rental-days-minus'),
    rentalDaysPlus: document.getElementById('rental-days-plus'),
    rentalEndDateDisplay: document.getElementById('rental-end-date-display'),
    rentalCollectionTime: document.getElementById('rental-collection-time'),
    rentalReturnTime: document.getElementById('rental-return-time'),
    rentalDurationDisplay: document.getElementById('rental-duration-display'),
    rentalTotalDisplay: document.getElementById('rental-total-display'),
    rentalDepositDisplay: document.getElementById('rental-deposit-display'),
    rentalDurationDaysHidden: document.getElementById('rental-duration-days'),
    rentalVariantHidden: document.getElementById('rental-variant-hidden'),
    rentalDepositHidden: document.getElementById('rental-deposit-hidden'),
    rentalDeliveryMethodHidden: document.getElementById('rental-delivery-method-hidden'),
    isRentalHidden: document.getElementById('is-rental-hidden'),

    // Buttons
    btnSubmit: document.getElementById('btn-submit'),

    // Success
    formSuccess: document.getElementById('form-success'),
    successName: document.getElementById('success-name'),
    successProduct: document.getElementById('success-product'),
    successEmail: document.getElementById('success-email'),
    successMethod: document.getElementById('success-method'),
    successWhatsappLink: document.querySelector('.ir-order-form__whatsapp')
  };

  // ═══════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════
  let activeTab = 'sales';
  let isDelivery = false;
  let isRentalUber = false;
  let selectedProducts = []; // Array of selected product objects with quantities
  let productQuantities = {}; // { handle: quantity }
  let shippingCost = 0;

  // Rental state — supports multiple rental products
  let rentalState = {
    isRental: false,
    products: [], // Array of { handle, title, variantId, variantTitle, quantity, weeklyPrice, monthlyPrice, deposit }
    startDate: null,
    endDate: null,
    collectionTime: null,
    returnTime: null,
    durationDays: 0,
    calculatedPrice: 0,
    totalDeposit: 0,
    hasError: false,
    errorMessage: ''
  };

  // ═══════════════════════════════════════════
  // UTILITY FUNCTIONS
  // ═══════════════════════════════════════════
  function formatCurrency(amount) {
    return 'R ' + amount.toLocaleString('en-ZA');
  }

  function getSelectedOption(select) {
    return select.options[select.selectedIndex];
  }

  // Get all checked product checkboxes
  function getSelectedProductCheckboxes() {
    return Array.from(elements.productCheckboxes).filter(cb => cb.checked);
  }

  // ═══════════════════════════════════════════
  // TAB SWITCHING
  // ═══════════════════════════════════════════
  function switchTab(tab) {
    if (tab === activeTab) return;
    activeTab = tab;

    // Update tab buttons
    elements.tabs.forEach(t => {
      if (t.dataset.tab === tab) {
        t.classList.add('ir-order-form__tab--active');
      } else {
        t.classList.remove('ir-order-form__tab--active');
      }
    });

    // Update hidden input
    if (elements.orderTypeHidden) {
      elements.orderTypeHidden.value = tab;
    }

    // Update URL without scrolling or reloading
    const url = new URL(window.location.href);
    if (tab === 'rentals') {
      url.searchParams.set('tab', 'rentals');
    } else {
      url.searchParams.set('tab', 'buy');
    }
    // Preserve other params like product, days
    history.replaceState({ tab: tab }, '', url.toString());

    // Show/hide product categories
    document.querySelectorAll('.ir-order-form__product-category').forEach(cat => {
      const catType = cat.dataset.tabCategory;
      if (catType === tab) {
        cat.style.display = '';
      } else {
        cat.style.display = 'none';
        // Also uncheck any products in hidden categories
        cat.querySelectorAll('input[name="product-checkbox"]').forEach(cb => {
          if (cb.checked) {
            cb.checked = false;
            toggleQuantityControls(cb.dataset.handle, false);
          }
        });
      }
    });

    // Show/hide fulfillment sections
    if (tab === 'sales') {
      // Show sales fulfillment, hide rental fulfillment
      const salesFulfillment = document.getElementById('sales-fulfillment');
      if (salesFulfillment) salesFulfillment.style.display = '';
      if (elements.rentalFulfillment) elements.rentalFulfillment.style.display = 'none';
      if (elements.rentalUberFields) elements.rentalUberFields.style.display = 'none';

      // Reset rental state
      rentalState.isRental = false;
      isRentalUber = false;
      rentalState.products = [];
      if (elements.isRentalHidden) elements.isRentalHidden.value = 'false';
      if (elements.rentalFieldsStep2) elements.rentalFieldsStep2.style.display = 'none';

      // Restore sales delivery state
      if (isDelivery && elements.addressFields) {
        showElement(elements.addressFields);
        showElement(elements.provinceField);
      }
    } else {
      // Hide sales fulfillment, show rental fulfillment
      const salesFulfillment = document.getElementById('sales-fulfillment');
      if (salesFulfillment) salesFulfillment.style.display = 'none';
      if (elements.provinceField) elements.provinceField.style.display = 'none';
      if (elements.shippingCostDisplay) elements.shippingCostDisplay.style.display = 'none';
      if (elements.addressFields) elements.addressFields.style.display = 'none';
      if (elements.rentalFulfillment) elements.rentalFulfillment.style.display = 'block';

      rentalState.isRental = true;
      if (elements.isRentalHidden) elements.isRentalHidden.value = 'true';
    }

    // Clear any previously selected products
    selectedProducts = [];
    productQuantities = {};
    updateSelectedProducts();
  }

  // Build product object from checkbox
  function buildProductFromCheckbox(checkbox) {
    const handle = checkbox.dataset.handle;
    const quantity = productQuantities[handle] || 1;
    const isRental = checkbox.dataset.productType === 'rental';

    let variantId = checkbox.dataset.variantId || null;
    let variantTitle = null;
    let variantPrice = 0;

    // For rentals, get variant from the rental radio selector
    if (isRental) {
      const variant = getSelectedRentalVariant(handle);
      if (variant) {
        variantId = variant.id;
        variantTitle = variant.title;
        variantPrice = variant.price;
      }
    } else if (checkbox.dataset.hasVariants === 'true') {
      // For products with multiple variants, get from product variant selector
      const variant = getSelectedProductVariant(handle);
      if (variant) {
        variantId = variant.id;
        variantTitle = variant.title;
        variantPrice = variant.price;
      }
    }

    return {
      title: checkbox.value,
      price: checkbox.dataset.price,
      priceRaw: parseInt(checkbox.dataset.priceRaw) || 0,
      handle: handle,
      shippingCategory: checkbox.dataset.shippingCategory || defaultCategory,
      image: checkbox.dataset.image,
      type: checkbox.dataset.productType,
      quantity: quantity,
      isRental: isRental,
      variantId: variantId,
      variantTitle: variantTitle,
      variantPrice: variantPrice
    };
  }

  // Get quantity for a product
  function getProductQuantity(handle) {
    return productQuantities[handle] || 1;
  }

  // Set quantity for a product
  function setProductQuantity(handle, quantity) {
    if (quantity < 1) quantity = 1;
    if (quantity > 10) quantity = 10; // Max 10 units
    productQuantities[handle] = quantity;

    // Update the quantity display
    const qtyDisplay = document.querySelector(`.ir-order-form__qty-value[data-handle="${handle}"]`);
    if (qtyDisplay) {
      qtyDisplay.textContent = quantity;
    }

    // Update totals
    updateSelectedProducts();
  }

  // Show/hide quantity controls based on checkbox state
  function toggleQuantityControls(handle, show) {
    const controls = document.querySelector(`.ir-order-form__quantity-controls[data-handle="${handle}"]`);
    if (controls) {
      controls.style.display = show ? 'flex' : 'none';
    }

    // Initialize quantity to 1 when showing
    if (show && !productQuantities[handle]) {
      productQuantities[handle] = 1;
    }
  }

  // ═══════════════════════════════════════════
  // RENTAL LOGIC
  // ═══════════════════════════════════════════
  function initRentalDateInputs() {
    if (!elements.rentalStartDate || !elements.rentalDaysInput) return;

    // Set minimum start date to today
    const today = new Date();
    const pad = (n) => n.toString().padStart(2, '0');
    const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

    elements.rentalStartDate.min = todayStr;

    // Default to tomorrow for start date
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    elements.rentalStartDate.value = `${tomorrow.getFullYear()}-${pad(tomorrow.getMonth() + 1)}-${pad(tomorrow.getDate())}`;

    // Event listeners
    elements.rentalStartDate.addEventListener('change', updateRentalCalculation);
    if (elements.rentalCollectionTime) {
      elements.rentalCollectionTime.addEventListener('change', handleTimeChange);
    }
    if (elements.rentalReturnTime) {
      elements.rentalReturnTime.addEventListener('change', handleTimeChange);
    }

    // Day picker buttons
    if (elements.rentalDaysMinus) {
      elements.rentalDaysMinus.addEventListener('click', function() {
        let val = parseInt(elements.rentalDaysInput.value, 10) || 1;
        if (val > 1) {
          elements.rentalDaysInput.value = val - 1;
          updateRentalCalculation();
        }
      });
    }
    if (elements.rentalDaysPlus) {
      elements.rentalDaysPlus.addEventListener('click', function() {
        let val = parseInt(elements.rentalDaysInput.value, 10) || 1;
        if (val < 90) {
          elements.rentalDaysInput.value = val + 1;
          updateRentalCalculation();
        }
      });
    }
    if (elements.rentalDaysInput) {
      elements.rentalDaysInput.addEventListener('change', function() {
        let val = parseInt(elements.rentalDaysInput.value, 10);
        if (isNaN(val) || val < 1) val = 1;
        if (val > 90) val = 90;
        elements.rentalDaysInput.value = val;
        updateRentalCalculation();
      });
      elements.rentalDaysInput.addEventListener('input', function() {
        let val = parseInt(elements.rentalDaysInput.value, 10);
        if (!isNaN(val) && val >= 1 && val <= 90) {
          updateRentalCalculation();
        }
      });
    }

    // Initial calculation
    updateRentalCalculation();
  }

  function handleTimeChange() {
    if (elements.rentalCollectionTime) {
      rentalState.collectionTime = elements.rentalCollectionTime.value;
    }
    if (elements.rentalReturnTime) {
      rentalState.returnTime = elements.rentalReturnTime.value;
    }
  }

  function formatDateWithDay(date) {
    const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  }

  function calculateEndDate(startStr, days) {
    if (!startStr || !days) return null;
    const start = new Date(startStr);
    const end = new Date(start);
    end.setDate(end.getDate() + parseInt(days, 10));
    return end;
  }

  function initRentalFulfillment() {
    // Rental fulfillment toggle
    const rentalFulfillmentRadios = document.querySelectorAll('input[name="Rental Fulfillment Method"]');
    rentalFulfillmentRadios.forEach(radio => {
      radio.addEventListener('change', function() {
        if (this.value === 'uber-delivery') {
          isRentalUber = true;
          showElement(elements.rentalUberFields);
          if (elements.rentalDeliveryMethodHidden) {
            elements.rentalDeliveryMethodHidden.value = 'uber-delivery';
          }
        } else {
          isRentalUber = false;
          hideElement(elements.rentalUberFields);
          if (elements.rentalDeliveryMethodHidden) {
            elements.rentalDeliveryMethodHidden.value = 'collection';
          }
        }
      });
    });
  }

  function updateRentalCalculation() {
    const startVal = elements.rentalStartDate ? elements.rentalStartDate.value : '';
    const daysVal = elements.rentalDaysInput ? parseInt(elements.rentalDaysInput.value, 10) : 0;

    if (!startVal || !daysVal || daysVal < 1) {
      if (elements.rentalDurationDisplay) elements.rentalDurationDisplay.value = '';
      if (elements.rentalTotalDisplay) elements.rentalTotalDisplay.value = '';
      if (elements.rentalEndDateDisplay) elements.rentalEndDateDisplay.textContent = 'Select start date and days';
      rentalState.startDate = null;
      rentalState.endDate = null;
      rentalState.durationDays = 0;
      rentalState.calculatedPrice = 0;
      rentalState.hasError = false;
      rentalState.errorMessage = '';
      updateSummary();
      return;
    }

    const startDate = new Date(startVal);
    const endDate = calculateEndDate(startVal, daysVal);

    rentalState.startDate = startVal;
    rentalState.endDate = endDate ? endDate.toISOString().split('T')[0] : '';
    rentalState.durationDays = daysVal;
    rentalState.hasError = false;
    rentalState.errorMessage = '';

    // Update return date display
    if (elements.rentalEndDateDisplay && endDate) {
      elements.rentalEndDateDisplay.textContent = formatDateWithDay(endDate);
      elements.rentalEndDateDisplay.style.color = 'var(--ir-green)';
    }

    // Calculate total across ALL rental products
    let totalPrice = 0;
    let totalDeposit = 0;

    const checkedBoxes = getSelectedProductCheckboxes();
    const rentalCheckboxes = checkedBoxes.filter(cb => cb.dataset.productType === 'rental');

    rentalCheckboxes.forEach(cb => {
      const handle = cb.dataset.handle;
      const qty = productQuantities[handle] || 1;
      const prices = getRentalVariantPrices(handle);
      const deposit = parseInt(cb.dataset.rentalDeposit) || 0;
      const result = calculateRentalPrice(daysVal, prices.weekly, prices.monthly);
      totalPrice += result.price * qty;
      totalDeposit += deposit * qty;

      // Auto-select matching variant radio
      const variantRadios = document.querySelectorAll(`input[name="rental-variant-${handle}"]`);
      variantRadios.forEach(r => {
        const title = (r.dataset.variantTitle || '').toLowerCase();
        if (result.variant === 'weekly' && title.includes('week')) {
          r.checked = true;
        } else if (result.variant === 'monthly' && title.includes('month')) {
          r.checked = true;
        }
      });
    });

    rentalState.calculatedPrice = totalPrice;
    rentalState.totalDeposit = totalDeposit;

    // Update display
    if (elements.rentalDurationDisplay) {
      elements.rentalDurationDisplay.value = `${daysVal} day${daysVal === 1 ? '' : 's'}`;
    }
    if (elements.rentalTotalDisplay) {
      elements.rentalTotalDisplay.value = formatCurrency(totalPrice);
    }
    if (elements.rentalDepositDisplay) {
      elements.rentalDepositDisplay.value = formatCurrency(totalDeposit);
    }

    // Update hidden fields
    if (elements.rentalDurationDaysHidden) elements.rentalDurationDaysHidden.value = daysVal;
    if (elements.rentalDepositHidden) elements.rentalDepositHidden.value = totalDeposit;

    updateSummary();
  }

  function checkRentalSelection() {
    const checkedBoxes = getSelectedProductCheckboxes();
    const rentalCheckboxes = checkedBoxes.filter(cb => cb.dataset.productType === 'rental');

    if (rentalCheckboxes.length > 0) {
      // Build rental products array
      rentalState.products = rentalCheckboxes.map(cb => {
        const handle = cb.dataset.handle;
        const variant = getSelectedRentalVariant(handle);
        const prices = getRentalVariantPrices(handle);
        return {
          handle: handle,
          title: cb.value,
          variantId: variant ? variant.id : null,
          variantTitle: variant ? variant.title : null,
          quantity: productQuantities[handle] || 1,
          weeklyPrice: prices.weekly,
          monthlyPrice: prices.monthly,
          deposit: parseInt(cb.dataset.rentalDeposit) || 0
        };
      });

      rentalState.isRental = true;
      if (elements.isRentalHidden) {
        elements.isRentalHidden.value = 'true';
      }

      // Show rental fields in Step 2
      showElement(elements.rentalFieldsStep2);
    } else {
      rentalState.isRental = false;
      rentalState.products = [];
      rentalState.startDate = null;
      rentalState.endDate = null;
      rentalState.collectionTime = null;
      rentalState.returnTime = null;
      rentalState.durationDays = 0;
      rentalState.calculatedPrice = 0;
      rentalState.totalDeposit = 0;

      // Hide rental fields in Step 2
      hideElement(elements.rentalFieldsStep2);

      // Clear rental fields
      if (elements.rentalStartDate) elements.rentalStartDate.value = '';
      if (elements.rentalDaysInput) elements.rentalDaysInput.value = '1';
      if (elements.rentalEndDateDisplay) elements.rentalEndDateDisplay.textContent = 'Select start date and days';
      if (elements.rentalCollectionTime) elements.rentalCollectionTime.value = '';
      if (elements.rentalReturnTime) elements.rentalReturnTime.value = '';
      if (elements.rentalDurationDisplay) elements.rentalDurationDisplay.value = '';
      if (elements.rentalTotalDisplay) elements.rentalTotalDisplay.value = '';
      if (elements.rentalDepositDisplay) elements.rentalDepositDisplay.value = '';

      if (elements.rentalDurationDaysHidden) elements.rentalDurationDaysHidden.value = '';
      if (elements.rentalVariantHidden) elements.rentalVariantHidden.value = '';
      if (elements.rentalDepositHidden) elements.rentalDepositHidden.value = '';
      if (elements.isRentalHidden) elements.isRentalHidden.value = 'false';
    }

    updateRentalCalculation();
  }

  // ═══════════════════════════════════════════
  // PRODUCT SELECTION & UPDATE
  // ═══════════════════════════════════════════
  function updateSelectedProducts() {
    const checkedBoxes = getSelectedProductCheckboxes();
    selectedProducts = checkedBoxes.map(buildProductFromCheckbox);

    // Show/hide quantity controls based on selection
    elements.productCheckboxes.forEach(checkbox => {
      const handle = checkbox.dataset.handle;
      toggleQuantityControls(handle, checkbox.checked);
    });

    // Check rental status
    checkRentalSelection();

    // Update hidden form fields
    updateHiddenFields();

    // Update preview section
    updateSelectedProductsPreview();

    // Update delivery price badge
    updateDeliveryPriceBadge();

    // Recalculate shipping
    calculateShipping();
  }

  // Update hidden form fields with selected products
  function updateHiddenFields() {
    const productTitles = selectedProducts.map(p =>
      p.quantity > 1 ? `${p.title} x${p.quantity}` : p.title
    ).join(', ');
    const productDetails = selectedProducts.map(p => {
      let name = p.variantTitle ? `${p.title} (${p.variantTitle})` : p.title;
      return p.quantity > 1
        ? `${name} x${p.quantity} (${p.price} each)`
        : `${name} (${p.price})`;
    }).join(' | ');

    if (elements.productsHidden) {
      elements.productsHidden.value = productTitles;
    }
    if (elements.productsDetailsHidden) {
      elements.productsDetailsHidden.value = productDetails;
    }
  }

  // Update the selected products preview section
  function updateSelectedProductsPreview() {
    if (selectedProducts.length === 0) {
      hideElement(elements.selectedProductsPreview);
      hideElement(elements.orderSummary);
      return;
    }

    // Calculate total item count (sum of quantities)
    const totalItemCount = selectedProducts.reduce((sum, p) => sum + p.quantity, 0);

    // Update count
    if (elements.selectedCount) {
      elements.selectedCount.textContent = totalItemCount;
    }

    // Build product list HTML with quantities
    if (elements.selectedProductsList) {
      elements.selectedProductsList.innerHTML = selectedProducts.map(product => {
        let lineTotal;
        if (product.isRental && rentalState.durationDays > 0) {
          const prices = getRentalVariantPrices(product.handle);
          const result = calculateRentalPrice(rentalState.durationDays, prices.weekly, prices.monthly);
          lineTotal = result.price * product.quantity;
        } else {
          lineTotal = (product.priceRaw / 100) * product.quantity;
        }
        let title = product.title;
        if (product.variantTitle) {
          title += ` (${product.variantTitle})`;
        }
        return `
          <div class="ir-order-form__selected-item">
            <div class="ir-order-form__selected-item-image">
              ${product.image ? `<img src="${product.image}" alt="${product.title}">` : ''}
            </div>
            <div class="ir-order-form__selected-item-details">
              <span class="ir-order-form__selected-item-name">${title}</span>
              ${product.isRental && rentalState.durationDays > 0 ? `<span style="font-size:12px;color:var(--ir-text-muted);">${rentalState.durationDays} days</span>` : ''}
            </div>
            ${product.quantity > 1 ? `<span class="ir-order-form__selected-item-qty">×${product.quantity}</span>` : ''}
            <span class="ir-order-form__selected-item-price">${formatCurrency(lineTotal)}</span>
          </div>
        `;
      }).join('');
    }

    // Calculate and show subtotal
    let subtotal = 0;
    selectedProducts.forEach(p => {
      if (p.isRental && rentalState.durationDays > 0) {
        const prices = getRentalVariantPrices(p.handle);
        const result = calculateRentalPrice(rentalState.durationDays, prices.weekly, prices.monthly);
        subtotal += result.price * p.quantity;
      } else {
        subtotal += (p.priceRaw / 100) * p.quantity;
      }
    });

    if (elements.productsSubtotal) {
      elements.productsSubtotal.textContent = formatCurrency(subtotal);
    }

    showElement(elements.selectedProductsPreview);
    updateSummary();
  }

  function showElement(el) {
    if (el) {
      el.style.display = '';
      el.classList.add('is-visible');
    }
  }

  function hideElement(el) {
    if (el) {
      el.style.display = 'none';
      el.classList.remove('is-visible');
    }
  }

  // ═══════════════════════════════════════════
  // SHIPPING CALCULATION
  // ═══════════════════════════════════════════
  function calculateShipping() {
    // Rentals don't have shipping costs
    if (rentalState.isRental) {
      shippingCost = 0;
      hideElement(elements.shippingCostDisplay);
      updateSummary();
      return;
    }

    if (!isDelivery || selectedProducts.length === 0) {
      shippingCost = 0;
      hideElement(elements.shippingCostDisplay);
      updateSummary();
      return;
    }

    const province = elements.provinceSelect.value;
    if (!province) {
      shippingCost = 0;
      hideElement(elements.shippingCostDisplay);
      updateSummary();
      return;
    }

    const provinceOption = getSelectedOption(elements.provinceSelect);
    const region = provinceOption.dataset.region || 'other';

    // For multiple products, use the highest shipping category
    let highestPriority = 0;
    let highestCategory = defaultCategory;

    selectedProducts.forEach(product => {
      const category = product.shippingCategory || defaultCategory;
      const priority = categoryPriority[category] || 0;
      if (priority > highestPriority) {
        highestPriority = priority;
        highestCategory = category;
      }
    });

    // Get shipping cost based on highest category
    const categoryRates = shippingCosts[highestCategory] || shippingCosts[defaultCategory];
    shippingCost = categoryRates[region] || categoryRates['other'];

    // Calculate total item count (sum of all quantities)
    const totalItemCount = selectedProducts.reduce((sum, p) => sum + p.quantity, 0);

    // If multiple items total, add per-item handling fee (except for first)
    if (totalItemCount > 1) {
      shippingCost += (totalItemCount - 1) * 50;
    }

    elements.shippingCostValue.textContent = formatCurrency(shippingCost);
    elements.shippingCostHidden.value = formatCurrency(shippingCost);
    showElement(elements.shippingCostDisplay);

    updateSummary();
  }

  function updateDeliveryPriceBadge() {
    if (selectedProducts.length === 0) {
      elements.deliveryPriceBadge.textContent = 'from R200';
      return;
    }

    // Rentals show no shipping
    if (rentalState.isRental) {
      elements.deliveryPriceBadge.textContent = 'FREE';
      return;
    }

    // Show min price based on selected products
    let highestPriority = 0;
    let highestCategory = defaultCategory;

    selectedProducts.forEach(product => {
      const category = product.shippingCategory || defaultCategory;
      const priority = categoryPriority[category] || 0;
      if (priority > highestPriority) {
        highestPriority = priority;
        highestCategory = category;
      }
    });

    const minPrice = shippingCosts[highestCategory] ? shippingCosts[highestCategory]['western-cape'] : 200;
    elements.deliveryPriceBadge.textContent = 'from ' + formatCurrency(minPrice);
  }

  // ═══════════════════════════════════════════
  // SUMMARY UPDATE
  // ═══════════════════════════════════════════
  function updateSummary() {
    if (selectedProducts.length === 0) {
      hideElement(elements.orderSummary);
      return;
    }

    // Calculate total with quantities
    let productsTotal = 0;
    selectedProducts.forEach(p => {
      if (p.isRental && rentalState.durationDays > 0) {
        const prices = getRentalVariantPrices(p.handle);
        const result = calculateRentalPrice(rentalState.durationDays, prices.weekly, prices.monthly);
        productsTotal += result.price * p.quantity;
      } else {
        productsTotal += (p.priceRaw / 100) * p.quantity;
      }
    });

    const totalItemCount = selectedProducts.reduce((sum, p) => sum + p.quantity, 0);

    // Show product names with quantities and variants
    let productNames;
    if (selectedProducts.length === 1 && selectedProducts[0].quantity === 1) {
      productNames = selectedProducts[0].variantTitle
        ? `${selectedProducts[0].title} (${selectedProducts[0].variantTitle})`
        : selectedProducts[0].title;
    } else if (totalItemCount <= 3) {
      productNames = selectedProducts.map(p => {
        let name = p.variantTitle ? `${p.title} (${p.variantTitle})` : p.title;
        return p.quantity > 1 ? `${name} ×${p.quantity}` : name;
      }).join(', ');
    } else {
      productNames = `${totalItemCount} items (${selectedProducts.length} products)`;
    }

    elements.summaryProduct.textContent = productNames;
    elements.summaryProductPrice.textContent = formatCurrency(productsTotal);

    if (isDelivery && shippingCost > 0 && !rentalState.isRental) {
      elements.summaryShipping.textContent = formatCurrency(shippingCost);
      showElement(elements.summaryShippingRow);
      elements.summaryTotal.textContent = formatCurrency(productsTotal + shippingCost);
    } else {
      hideElement(elements.summaryShippingRow);
      elements.summaryTotal.textContent = formatCurrency(productsTotal);
    }

    // Add deposit note for rentals
    updateDepositNote();

    showElement(elements.orderSummary);
  }

  function updateDepositNote() {
    const existingNote = document.getElementById('rental-deposit-note');
    if (existingNote) existingNote.remove();

    if (rentalState.isRental && rentalState.calculatedPrice > 0) {
      const summary = elements.orderSummary;
      if (!summary) return;

      const note = document.createElement('p');
      note.id = 'rental-deposit-note';
      note.style.cssText = 'font-size:12px;color:var(--ir-text-muted);margin-top:8px;font-style:italic;';
      note.textContent = 'Refundable deposit required. Anthony will confirm details.';
      summary.appendChild(note);
    }
  }

  // ═══════════════════════════════════════════
  // FORM VALIDATION
  // ═══════════════════════════════════════════
  function validateForm() {
    // 1. At least one product selected
    if (selectedProducts.length === 0) {
      if (elements.productSelector) {
        elements.productSelector.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      alert('Please select at least one product');
      return false;
    }

    // 2. Province if sales delivery
    if (activeTab === 'sales' && isDelivery && !elements.provinceSelect.value) {
      elements.provinceSelect.focus();
      alert('Please select your province for delivery');
      return false;
    }

    // 3. Required personal fields
    const requiredFields = [
      { el: elements.nameInput, name: 'Full name' },
      { el: elements.emailInput, name: 'Email' },
      { el: elements.phoneInput, name: 'Phone number' }
    ];

    // Add address fields if sales delivery
    if (activeTab === 'sales' && isDelivery) {
      requiredFields.push(
        { el: elements.streetInput, name: 'Street address' },
        { el: elements.suburbInput, name: 'Suburb' },
        { el: elements.cityInput, name: 'City' },
        { el: elements.postalInput, name: 'Postal code' }
      );
    }

    // Add Uber delivery address if rental Uber delivery
    if (activeTab === 'rentals' && isRentalUber) {
      requiredFields.push(
        { el: elements.rentalDeliveryAddress, name: 'Delivery address' },
        { el: elements.rentalDeliverySuburb, name: 'Delivery suburb' },
        { el: elements.rentalDeliveryCity, name: 'Delivery city' }
      );
    }

    // Add company name if business purchase
    if (elements.businessCheckbox.checked) {
      requiredFields.push(
        { el: elements.companyInput, name: 'Company name' }
      );
    }

    for (const field of requiredFields) {
      if (!field.el.value.trim()) {
        field.el.focus();
        field.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        alert('Please enter your ' + field.name.toLowerCase());
        return false;
      }
    }

    // 4. Rental dates and times if rental selected
    if (rentalState.isRental) {
      if (!elements.rentalStartDate.value) {
        alert('Please select a collection date');
        if (elements.rentalFieldsStep2) {
          elements.rentalFieldsStep2.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return false;
      }
      const days = parseInt(elements.rentalDaysInput.value, 10);
      if (!days || days < 1) {
        alert('Please select number of rental days');
        if (elements.rentalFieldsStep2) {
          elements.rentalFieldsStep2.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return false;
      }
      if (!elements.rentalCollectionTime.value || !elements.rentalReturnTime.value) {
        alert('Please select collection and return times');
        if (elements.rentalFieldsStep2) {
          elements.rentalFieldsStep2.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return false;
      }
      if (rentalState.hasError) {
        alert(rentalState.errorMessage || 'Please fix the rental date errors');
        if (elements.rentalFieldsStep2) {
          elements.rentalFieldsStep2.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return false;
      }
    }

    // 5. Email format — strict enough to match Shopify's draftOrderCreate rules
    // (server re-checks at layer 3; this regex mirrors backend EMAIL_DOMAIN_RE)
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/;
    if (!emailRegex.test(elements.emailInput.value)) {
      elements.emailInput.focus();
      elements.emailInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      alert("Please check your email address — it doesn't look valid.");
      return false;
    }

    return true;
  }

  // ═══════════════════════════════════════════
  // FORM SUBMISSION
  // ═══════════════════════════════════════════

  // Backend API endpoint
  const BACKEND_API_URL = 'https://api.iroll.co.za/api/order-form';

  function handleSubmit(e) {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Show loading state
    const btnText = elements.btnSubmit.querySelector('.btn-text');
    const btnLoading = elements.btnSubmit.querySelector('.btn-loading');
    const btnArrow = elements.btnSubmit.querySelector('.btn-arrow');

    btnText.style.display = 'none';
    btnArrow.style.display = 'none';
    btnLoading.style.display = 'inline-flex';
    elements.btnSubmit.disabled = true;

    // Get form data
    const formData = new FormData(form);

    // Build payload for backend API - supports multiple products with quantities
    let productsTotal = 0;
    const productsPayload = selectedProducts.map(p => {
      let price;
      if (p.isRental && rentalState.durationDays > 0) {
        const prices = getRentalVariantPrices(p.handle);
        const result = calculateRentalPrice(rentalState.durationDays, prices.weekly, prices.monthly);
        price = result.price;
      } else {
        price = p.priceRaw / 100;
      }
      productsTotal += price * p.quantity;
      return {
        handle: p.handle,
        title: p.title,
        price: price,
        quantity: p.quantity,
        product_type: p.type,
        variant_id: p.variantId,
        image: p.image
      };
    });

    const payload = {
      // Multi-product support with quantities
      products: productsPayload,
      // Legacy single product fields (for backwards compatibility)
      product_handle: selectedProducts[0]?.handle || '',
      product_title: selectedProducts.map(p =>
        p.quantity > 1 ? `${p.title} ×${p.quantity}` : p.title
      ).join(', '),
      product_price: productsTotal,
      // Rest of the order info
      fulfillment_method: activeTab === 'rentals'
        ? (isRentalUber ? 'uber-delivery' : 'collection')
        : (formData.get('Fulfillment Method')?.includes('Delivery') ? 'delivery' : 'collection'),
      province: formData.get('Province') || null,
      shipping_cost: activeTab === 'rentals' ? 0 : (shippingCost || 0),
      full_name: formData.get('Full Name') || '',
      email: formData.get('Email') || '',
      phone: formData.get('Phone') || '',
      address1: formData.get('Street Address') || null,
      address2: formData.get('Suburb') || null,
      city: formData.get('City') || null,
      postal_code: formData.get('Postal Code') || null,
      is_business: !!formData.get('Business Purchase'),
      company_name: formData.get('Company Name') || null,
      vat_number: formData.get('VAT Number') || null,
      how_heard: formData.get('How Did You Hear About Us') || null,
      notes: formData.get('Notes') || null,
      // Attribution: use captured UTM/GCLID data. Do NOT fall back to the human
      // dropdown — that creates mixed machine/human labels and breaks reporting.
      attribution_source: (window.irollAttribution && window.irollAttribution.source) || null,
      attribution_medium: (window.irollAttribution && window.irollAttribution.medium) || null,
      attribution_campaign: (window.irollAttribution && window.irollAttribution.campaign) || null,
      attribution_gclid: (window.irollAttribution && window.irollAttribution.gclid) || null,
      attribution_referrer: (window.irollAttribution && window.irollAttribution.referrer) || null
    };

    // Add rental details if applicable
    if (rentalState.isRental) {
      payload.is_rental = true;
      payload.rental_start_date = rentalState.startDate || '';
      payload.rental_end_date = rentalState.endDate || '';
      payload.rental_collection_time = rentalState.collectionTime || '';
      payload.rental_return_time = rentalState.returnTime || '';
      payload.rental_duration_days = rentalState.durationDays || 0;
      payload.rental_variant = rentalState.products.map(rp => rp.variantTitle).filter(Boolean).join(', ') || '';
      payload.rental_deposit = rentalState.totalDeposit || 0;
      payload.rental_delivery_method = isRentalUber ? 'uber-delivery' : 'collection';
    }

    // Check if using Formspree (legacy) or Backend API
    const formspreeEndpoint = form.dataset.formEndpoint;

    if (formspreeEndpoint && formspreeEndpoint.includes('formspree.io')) {
      // Legacy: Submit to Formspree
      fetch(formspreeEndpoint, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      })
      .then(response => {
        if (response.ok) {
          showSuccess(formData);
        } else {
          throw new Error('Form submission failed');
        }
      })
      .catch(error => {
        console.error('Form error:', error);
        showFormError('There was an error submitting your request. Please try again or contact us on WhatsApp.');
        resetSubmitButton();
      });
    } else {
      // Submit to iRoll Backend API (creates draft order in Shopify)
      fetch(BACKEND_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            throw new Error(err.detail || 'Order submission failed');
          });
        }
        return response.json();
      })
      .then(data => {
        console.log('Order created:', data);
        showSuccess(formData, data);
      })
      .catch(error => {
        console.error('Order error:', error);
        showFormError('There was an error submitting your order: ' + error.message + ' Please try again or contact us on WhatsApp.');
        resetSubmitButton();
      });
    }
  }

  function showSuccess(formData, apiResponse) {
    // Hide the entire form and shipping info
    if (form) {
      form.classList.add('is-submitted');
      form.style.display = 'none';
    }
    const shippingInfo = document.querySelector('.ir-order-form__shipping-info');
    if (shippingInfo) shippingInfo.style.display = 'none';
    
    // Hide submit button container
    const btnContainer = document.querySelector('.ir-order-form__nav');
    if (btnContainer) btnContainer.style.display = 'none';
    
    // Hide section header
    const sectionHeader = document.querySelector('.ir-order-form__header');
    if (sectionHeader) sectionHeader.style.display = 'none';

    // Populate success message
    elements.successName.textContent = formData.get('Full Name').split(' ')[0];

    // Show selected products with quantities and variants
    const totalItemCount = selectedProducts.reduce((sum, p) => sum + p.quantity, 0);
    let productDisplay;
    if (selectedProducts.length === 1 && selectedProducts[0].quantity === 1) {
      productDisplay = selectedProducts[0].variantTitle
        ? `${selectedProducts[0].title} (${selectedProducts[0].variantTitle})`
        : selectedProducts[0].title;
    } else if (totalItemCount <= 3) {
      productDisplay = selectedProducts.map(p => {
        let name = p.variantTitle ? `${p.title} (${p.variantTitle})` : p.title;
        return p.quantity > 1 ? `${name} ×${p.quantity}` : name;
      }).join(', ');
    } else {
      productDisplay = `${totalItemCount} items`;
    }
    elements.successProduct.textContent = productDisplay;

    elements.successEmail.textContent = formData.get('Email');
    elements.successMethod.textContent = formData.get('Fulfillment Method');

    // Show address if delivery
    const addressRow = document.getElementById('success-address-row');
    const addressSpan = document.getElementById('success-address');
    const addrParts = [];
    const street = formData.get('Street Address');
    const suburb = formData.get('Suburb');
    const cityVal = formData.get('City');
    const provinceVal = formData.get('Province');
    const postal = formData.get('Postal Code');
    if (street) addrParts.push(street);
    if (suburb && suburb !== cityVal) addrParts.push(suburb);
    if (cityVal) addrParts.push(cityVal);
    if (provinceVal) addrParts.push(provinceVal);
    if (postal) addrParts.push(postal);
    const fulfillmentMethod = (formData.get('Fulfillment Method') || '').toLowerCase();
    if (addrParts.length > 0 && fulfillmentMethod.includes('delivery')) {
      addressSpan.textContent = addrParts.join(', ');
      addressRow.style.display = '';
    } else if (addressRow) {
      addressRow.style.display = 'none';
    }

    // Show note if present
    const noteRow = document.getElementById('success-note-row');
    const noteSpan = document.getElementById('success-note');
    const noteVal = formData.get('Notes');
    if (noteVal && noteVal.trim()) {
      noteSpan.textContent = noteVal.trim();
      noteRow.style.display = '';
    } else if (noteRow) {
      noteRow.style.display = 'none';
    }

    // Show total if available
    const totalRow = document.getElementById('success-total-row');
    const totalSpan = document.getElementById('success-total');
    if (totalRow && totalSpan && apiResponse && apiResponse.total) {
      totalSpan.textContent = formatCurrency(apiResponse.total);
      totalRow.style.display = '';
    } else if (totalRow) {
      totalRow.style.display = 'none';
    }

    // Show invoice reference from Xero
    const invoiceSpan = document.getElementById('success-invoice');
    if (apiResponse && apiResponse.xero_invoice_number) {
      invoiceSpan.textContent = apiResponse.xero_invoice_number;
    } else if (apiResponse && apiResponse.draft_order_name) {
      invoiceSpan.textContent = apiResponse.draft_order_name;
    }

    // Show PDF download button if we have an invoice number
    const pdfRow = document.getElementById('success-pdf-row');
    const pdfDownload = document.getElementById('success-pdf-download');
    const invNum = apiResponse && apiResponse.xero_invoice_number;
    if (pdfRow && pdfDownload && invNum) {
      pdfDownload.href = 'https://api.iroll.co.za/api/order-form/invoice/' + invNum + '/pdf';
      pdfRow.style.display = '';
    } else if (pdfRow) {
      pdfRow.style.display = 'none';
    }

    // Show success
    showElement(elements.formSuccess);

    // Scroll to success message
    elements.formSuccess.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function updateSuccessWhatsappLink(formData) {
    const whatsappLink = elements.successWhatsappLink;
    if (!whatsappLink) return;

    const phone = whatsappLink.href.match(/wa\.me\/(\d+)/);
    const phoneNumber = phone ? phone[1] : '27793463742';

    let message = `Hi Anthony, I just submitted an order request on iroll.co.za.`;
    message += `\n\nProduct: ${elements.successProduct.textContent}`;

    if (rentalState.isRental) {
      message += `\nRental: ${rentalState.startDate || ''} to ${rentalState.endDate || ''}`;
      message += `\nDuration: ${rentalState.durationDays} days`;
      message += `\nPrice: ${formatCurrency(rentalState.calculatedPrice)}`;
      message += `\nDeposit: ${formatCurrency(rentalState.totalDeposit)}`;
    }

    message += `\n\nName: ${formData.get('Full Name') || ''}`;
    message += `\nEmail: ${formData.get('Email') || ''}`;

    const encodedMessage = encodeURIComponent(message);
    whatsappLink.href = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
  }

  function resetSubmitButton() {
    const btnText = elements.btnSubmit.querySelector('.btn-text');
    const btnLoading = elements.btnSubmit.querySelector('.btn-loading');
    const btnArrow = elements.btnSubmit.querySelector('.btn-arrow');

    btnText.style.display = '';
    btnArrow.style.display = '';
    btnLoading.style.display = 'none';
    elements.btnSubmit.disabled = false;
  }

  function showFormError(message) {
    const banner = document.getElementById('form-error-banner');
    const msgSpan = document.getElementById('form-error-message');
    if (banner && msgSpan) {
      msgSpan.textContent = message;
      banner.style.display = 'flex';
      banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      alert(message);
    }
  }

  function hideFormError() {
    const banner = document.getElementById('form-error-banner');
    if (banner) banner.style.display = 'none';
  }

  function resetFormForNewOrder() {
    // Reset form fields
    form.reset();
    selectedProducts = [];
    shippingCost = 0;
    
    // Clear selected products UI
    updateSelectedProductsPreview();
    updateSummary();
    
    // Hide success, show form
    hideElement(elements.formSuccess);
    form.classList.remove('is-submitted');
    form.style.display = '';
    
    // Show header and nav
    const sectionHeader = document.querySelector('.ir-order-form__header');
    if (sectionHeader) sectionHeader.style.display = '';
    const btnContainer = document.querySelector('.ir-order-form__nav');
    if (btnContainer) btnContainer.style.display = '';
    const shippingInfo = document.querySelector('.ir-order-form__shipping-info');
    if (shippingInfo) shippingInfo.style.display = '';
    
    // Scroll to top
    form.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ═══════════════════════════════════════════
  // EVENT LISTENERS
  // ═══════════════════════════════════════════
  function initEventListeners() {
    // Product selection (checkboxes - multi-select)
    elements.productCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', updateSelectedProducts);
    });

    // Product variant radio changes (non-rental)
    document.querySelectorAll('.ir-order-form__variant-selector--product input[type="radio"]').forEach(radio => {
      radio.addEventListener('change', function() {
        const handle = this.name.replace('variant-', '');
        const checkbox = document.querySelector(`input[name="product-checkbox"][data-handle="${handle}"]`);
        if (checkbox) {
          checkbox.dataset.variantId = this.value;
          checkbox.dataset.price = formatCurrency(parseInt(this.dataset.variantPrice) / 100);
          checkbox.dataset.priceRaw = this.dataset.variantPrice;
          // Update displayed price in card
          const priceEl = checkbox.closest('.ir-order-form__product-option').querySelector('.ir-order-form__product-price');
          if (priceEl) {
            priceEl.textContent = formatCurrency(parseInt(this.dataset.variantPrice) / 100);
          }
        }
        updateSelectedProducts();
      });
    });

    // Rental variant radio changes
    document.querySelectorAll('input[type="radio"][name^="rental-variant-"]').forEach(radio => {
      radio.addEventListener('change', function() {
        updateSelectedProducts();
        updateRentalCalculation();
      });
    });

    // Quantity buttons (using event delegation)
    document.addEventListener('click', function(e) {
      const btn = e.target.closest('.ir-order-form__qty-btn');
      if (!btn) return;

      const controls = btn.closest('.ir-order-form__quantity-controls');
      if (!controls) return;

      const handle = controls.dataset.handle;
      const action = btn.dataset.action;
      const currentQty = getProductQuantity(handle);

      if (action === 'increase') {
        setProductQuantity(handle, currentQty + 1);
      } else if (action === 'decrease') {
        setProductQuantity(handle, currentQty - 1);
      }
    });

    // Fulfillment method toggle
    elements.fulfillmentRadios.forEach(radio => {
      radio.addEventListener('change', function() {
        isDelivery = this.value === 'Delivery';

        if (isDelivery && !rentalState.isRental) {
          showElement(elements.provinceField);
          showElement(elements.addressFields);

          // Make address fields required
          elements.streetInput.required = true;
          elements.suburbInput.required = true;
          elements.cityInput.required = true;
          elements.postalInput.required = true;
          elements.provinceSelect.required = true;
        } else {
          hideElement(elements.provinceField);
          hideElement(elements.addressFields);
          hideElement(elements.shippingCostDisplay);

          // Remove required from address fields
          elements.streetInput.required = false;
          elements.suburbInput.required = false;
          elements.cityInput.required = false;
          elements.postalInput.required = false;
          elements.provinceSelect.required = false;

          shippingCost = 0;
        }

        calculateShipping();
      });
    });

    // Tab switching
    elements.tabs.forEach(tab => {
      tab.addEventListener('click', function() {
        switchTab(this.dataset.tab);
      });
    });

    // Province selection
    elements.provinceSelect.addEventListener('change', calculateShipping);

    // Business purchase checkbox
    elements.businessCheckbox.addEventListener('change', function() {
      if (this.checked) {
        showElement(elements.businessFields);
        elements.companyInput.required = true;
      } else {
        hideElement(elements.businessFields);
        elements.companyInput.required = false;
      }
    });

    // Form submission
    form.addEventListener('submit', handleSubmit);

    // New Order button
    const newOrderBtn = document.getElementById('btn-new-order');
    if (newOrderBtn) {
      newOrderBtn.addEventListener('click', resetFormForNewOrder);
    }

    // Hide error banner when user interacts with form
    form.addEventListener('input', hideFormError);

  }

  // ═══════════════════════════════════════════
  // GAZEBO PHOTO INJECTION (fallback when Liquid doesn't render)
  // ═══════════════════════════════════════════
  function initGazeboPhoto() {
    const rentalFulfillment = document.getElementById('rental-fulfillment');
    if (!rentalFulfillment) return;

    // Only inject if photo isn't already there
    if (rentalFulfillment.querySelector('.ir-order-form__gazebo-photo')) return;

    const banner = rentalFulfillment.querySelector('.ir-order-form__gazebo-banner');
    if (!banner) return;

    const photoDiv = document.createElement('div');
    photoDiv.className = 'ir-order-form__gazebo-photo';
    photoDiv.innerHTML = `
      <img
        src="https://cdn.shopify.com/s/files/1/0323/9857/3707/files/a3f02c71-1dcf-4911-86b0-023651b54f18.jpg?v=1728932619"
        alt="iRoll Gazebo at Blouberg Beachfront"
        class="ir-order-form__gazebo-img"
        loading="lazy"
        width="800"
      >
      <span class="ir-order-form__gazebo-caption">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
        iRoll Gazebo — Blouberg Beachfront
      </span>
    `;
    banner.insertAdjacentElement('afterend', photoDiv);
  }

  // ═══════════════════════════════════════════
  // SINGLE PRODUCT MODE
  // ═══════════════════════════════════════════
  function enableSingleProductMode(targetHandle) {
    if (!targetHandle) return;

    const normalizedHandle = targetHandle.toLowerCase().trim();
    let matchedCheckbox = null;
    let matchedRow = null;
    let isRental = false;

    // Find the matching product checkbox
    for (const cb of elements.productCheckboxes) {
      const cbHandle = (cb.dataset.handle || '').toLowerCase();
      const cbValue = (cb.value || '').toLowerCase();
      if (cbHandle === normalizedHandle || cbValue.includes(normalizedHandle)) {
        matchedCheckbox = cb;
        matchedRow = cb.closest('.ir-order-form__product-row');
        isRental = cb.dataset.productType === 'rental';
        break;
      }
    }

    if (!matchedCheckbox || !matchedRow) return;

    // Hide tabs entirely
    const tabsContainer = document.getElementById('order-type-tabs');
    if (tabsContainer) tabsContainer.style.display = 'none';

    // Hide all product categories that don't contain our product
    document.querySelectorAll('.ir-order-form__product-category').forEach(cat => {
      const hasMatch = cat.querySelector(`[data-product-handle="${matchedCheckbox.dataset.handle}"]`);
      if (!hasMatch) {
        cat.style.display = 'none';
      } else {
        // Hide the category title for a cleaner single-product look
        const title = cat.querySelector('.ir-order-form__category-title');
        if (title) title.style.display = 'none';
      }
    });

    // Hide all other product rows within the matched category
    document.querySelectorAll('.ir-order-form__product-row').forEach(row => {
      if (row !== matchedRow) {
        row.style.display = 'none';
      }
    });

    // Set correct tab context
    activeTab = isRental ? 'rentals' : 'sales';
    if (elements.orderTypeHidden) elements.orderTypeHidden.value = activeTab;

    if (isRental) {
      // Rental mode: hide sales fulfillment, show rental fulfillment
      const salesFulfillment = document.getElementById('sales-fulfillment');
      if (salesFulfillment) salesFulfillment.style.display = 'none';
      if (elements.rentalFulfillment) elements.rentalFulfillment.style.display = 'block';
      rentalState.isRental = true;
      if (elements.isRentalHidden) elements.isRentalHidden.value = 'true';
    } else {
      // Sales mode: show sales fulfillment, hide rental fulfillment
      const salesFulfillment = document.getElementById('sales-fulfillment');
      if (salesFulfillment) salesFulfillment.style.display = '';
      if (elements.rentalFulfillment) elements.rentalFulfillment.style.display = 'none';
      if (elements.rentalUberFields) elements.rentalUberFields.style.display = 'none';
      rentalState.isRental = false;
      if (elements.isRentalHidden) elements.isRentalHidden.value = 'false';
      if (elements.rentalFieldsStep2) elements.rentalFieldsStep2.style.display = 'none';
    }

    // Auto-select the product
    matchedCheckbox.checked = true;

    // Add single-product styling class
    form.classList.add('ir-order-form--single-product');

    // Update section title
    const sectionTitle = form.querySelector('.ir-order-form__section-title');
    if (sectionTitle) {
      sectionTitle.textContent = isRental ? 'Confirm Your Rental' : 'Confirm Your Scooter';
    }

    // Trigger updates
    updateSelectedProducts();
  }

  // ═══════════════════════════════════════════
  // INITIALIZATION
  // ═══════════════════════════════════════════
  function init() {
    initEventListeners();
    initRentalDateInputs();
    initRentalFulfillment();
    initGazeboPhoto();

    // Set initial state
    isDelivery = elements.deliveryOption && elements.deliveryOption.checked;

    const urlParams = new URLSearchParams(window.location.search);
    const productParam = urlParams.get('product');

    if (productParam) {
      // Single product mode: scoped to one product, skip normal tab init
      enableSingleProductMode(productParam);
    } else {
      // Comprehensive mode: show all products with tabs
      const tabParam = urlParams.get('tab');
      if (tabParam === 'rentals') {
        switchTab('rentals');
      } else {
        switchTab('sales');
      }
    }

    // If URL has days param, pre-fill rental days
    const daysParam = urlParams.get('days');
    if (daysParam && elements.rentalDaysInput) {
      const days = parseInt(daysParam, 10);
      if (days && days >= 1 && days <= 90) {
        elements.rentalDaysInput.value = days;
        updateRentalCalculation();
      }
    }
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
